import type { OnChanges, SimpleChanges } from '@angular/core';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';

import { I18nService, TranslatePipe } from '@recruitee/i18n';
import type { BillingPlan } from '@recruitee/products-types';
import { BillingPlanTier } from '@recruitee/products-types';
import type { SelectOption } from '@recruitee/user-interface';
import { SelectComponent } from '@recruitee/user-interface';

import { findNextIntDivisibleBy } from '../../helpers/find-next-divisible';

@Component({
  selector: 'rt-job-slots-picker',
  templateUrl: './job-slots-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SelectComponent, TranslatePipe],
})
export class JobSlotsPickerComponent implements OnChanges {
  public static readonly CustomProductSlotsLimit = 100;
  public static readonly DefaultProductSlotsLimit = 50;
  public static readonly MaxSlotsToBuy = 1000;
  public static readonly MinSlotsToBuy = 10;

  @Input() public optionStepBy: number = 5;
  @Input()
  public set value(inputValue: number) {
    this._value = Math.max(JobSlotsPickerComponent.MinSlotsToBuy, inputValue);

    if (this._value !== inputValue) {
      this.slotsChange.emit(this._value);
    }
  }
  public get value() {
    return this._value;
  }
  @Input() public product: BillingPlan;
  @Input() public addContactWithSalesOption: boolean;
  @Input() public ownedSlots: number;
  @Input() public isManualSubscription: boolean;

  @Output() public slotsChange = new EventEmitter<number>();

  public options: SelectOption[];

  private _value: number;

  constructor(private i18n: I18nService) {}

  // eslint-disable-next-line max-lines-per-function
  public ngOnChanges(changes: SimpleChanges): void {
    const { optionStepBy } = this;
    const min = this.getMinSlots();
    const limit = Math.min(
      this.product.custom
        ? this.product.baseSlots + JobSlotsPickerComponent.CustomProductSlotsLimit
        : this.ownedSlots + JobSlotsPickerComponent.DefaultProductSlotsLimit,
      JobSlotsPickerComponent.MaxSlotsToBuy,
    );

    const max = Math.min(
      Math.max(limit, Number.isFinite(this.ownedSlots) ? this.ownedSlots + this.optionStepBy : 0),
      JobSlotsPickerComponent.MaxSlotsToBuy,
    );

    this.options = this.generateSlotOptions(min, max, optionStepBy);

    if (this.addContactWithSalesOption) {
      this.options = [
        ...this.options,
        {
          value: Number.POSITIVE_INFINITY,
          label: this.getSlotLabel(
            `${
              max % this.optionStepBy === 0 ? max : findNextIntDivisibleBy(max, this.optionStepBy)
            }+`,
          ),
        },
      ];
    }

    if (
      !this.isManualSubscription &&
      !this.options.find(opt => opt.value === this.product.baseSlots) &&
      !((<BillingPlan>this.product).tier === BillingPlanTier.Launch && this.product.baseSlots === 5)
    ) {
      this.options = [...this.options, this.getSlotOption(this.product.baseSlots)];
    }

    this.options = this.options.sort((a, b) => <number>a.value - <number>b.value);
  }

  private generateSlotOptions(min: number, max: number, step: number): SelectOption[] {
    return new Array(Math.floor(max / step))
      .fill(null)
      .map((_, i) => (i + 1) * step)
      .filter(value => value >= min)
      .map(value => this.getSlotOption(value));
  }

  private getSlotOption(value: number): SelectOption {
    return {
      label: this.getSlotLabel(value),
      value,
    };
  }

  private getSlotLabel(value: number | string) {
    return this.i18n.translate('app.pricing.job_slots_count', { count: value });
  }

  private getMinSlots() {
    return Math.max(
      this.isManualSubscription ? this.ownedSlots : this.product.baseSlots,
      JobSlotsPickerComponent.MinSlotsToBuy,
    );
  }
}
